import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  AU_STATES,
  BASE_STATIONS,
  CONDUCT_ITEMS,
  CURRENCY_LABEL,
  GRID_FORMATS,
  LOCATED_FORMATS,
  MAX_QUOTE_OPTIONS,
  QC_RULE_DEFS,
  acceptedOptionId,
  addOption,
  hasOptions,
  nextOptionLabel,
  optionAggregate,
  optionAreas,
  optionName,
  optionTitle,
  optionTotals,
  optionsOf,
  removeOption,
  BOUNDARY_DATUMS,
  FLIGHT_PLAN_DIR,
  areaFromPlanFiles,
  areaLineKm,
  blockNameOf,
  boundaryTableFromBnd,
  boundaryTableFromKml,
  buildQuoteDocument,
  can,
  canEditQuote,
  contractSnapshot,
  defaultChannelSet,
  documentFileName,
  findBlockPlanFiles,
  formatKmInt,
  formatMoney,
  jobNumberFromQuote,
  lineKmCheck,
  localIsoDate,
  parseBndFile,
  parseBoundaryKml,
  parseLnsFile,
  parseSumFile,
  paymentBalance,
  quoteTotals,
  validateArea,
  validateJobNumber,
  validateQuoteForSend,
  validateQuoteNumber,
  type ChargeKind,
  type ConductItem,
  type DeliverableSpec,
  type LinesFile,
  type QcRule,
  type Quote,
  type QuoteAggregate,
  type QuoteArea,
  type QuoteCharge,
  type QuoteOption,
  type QuoteRevision,
  type ValidationIssue,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import {
  CheckField,
  IssueList,
  NumberField,
  SelectField,
  TextArea,
  TextField,
} from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { browserAssets, downloadPdf } from '../documents/browserAssets.ts';
import { renderPdf } from '../documents/render.ts';
import {
  activeClauses,
  loadClients,
  loadQuote,
  loadRevisions,
  makeContract,
  newId,
  quoteNumbers,
  saveQuote,
  sendRevision,
  setRevisionStatus,
  type Clients,
  type QuoteDraft,
  type TenantDocuments,
} from './api.ts';
import { QUOTE_STATUS_LABEL, tenantQcDefaults } from './shared.ts';

export interface QuoteEditorProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly roles: readonly string[];
  readonly quoteId: string;
  readonly docs: TenantDocuments;
  readonly onBack: () => void;
  readonly onOpenContract: (contractId: string) => void;
}

export const STEPS = ['Client', 'Areas', 'Specification', 'QC limits', 'Pricing', 'Review'] as const;
type Step = (typeof STEPS)[number];

/** Which step owns a validation field. */
export function stepOf(field: string): Step {
  if (field.startsWith('option')) return 'Areas';
  if (field.startsWith('area') || field === 'areas') {
    return field.endsWith('rate_per_line_km') ? 'Pricing' : 'Areas';
  }
  if (field.startsWith('qc.')) return 'QC limits';
  if (field.startsWith('charge') || /^(pp1|pp2|balance)_pct$/.test(field)) return 'Pricing';
  if (field.startsWith('sampling') || field.startsWith('deliverable')) return 'Specification';
  return 'Client';
}

const CONDUCT_LABEL: Record<ConductItem, string> = {
  personnel_equipment: 'Suitable personnel and equipment',
  fuel_truck: 'A fuel truck at the field base',
  fuel_positioning: 'Fuel positioning',
  accommodation: 'Accommodation for the crew',
  meals: 'Meals for the crew',
  transport: 'Transport for the crew and equipment',
  supplies: 'Supplies',
};

const errorFor = (issues: readonly ValidationIssue[], field: string): string | undefined =>
  issues.find((i) => i.field === field)?.message;

/**
 * The guided quote editor (TJR inventory 01 §10.5): Client → Areas → Specification → QC limits → Pricing → Review.
 * Every step saves the draft; Review shows the checks before sending, the quote PDF, the sent revisions and
 * "Make contract" from the accepted one. Office staff open it read-only with prices (decision 121).
 */
export function QuoteEditor({
  sb,
  tenantId,
  userId,
  roles,
  quoteId,
  docs,
  onBack,
  onOpenContract,
}: QuoteEditorProps): ReactElement {
  const [loaded, setLoaded] = useState<QuoteDraft | null>(null);
  const [draft, setDraft] = useState<QuoteDraft | null>(null);
  const [clients, setClients] = useState<Clients | null>(null);
  const [revisions, setRevisions] = useState<QuoteRevision[]>([]);
  const [numbers, setNumbers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('Client');
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** The option being edited in Areas and Pricing (decision 171). */
  const [optionId, setOptionId] = useState<string | null>(null);
  const send = can(roles, 'quote.send');

  const reload = useCallback(() => {
    setError(null);
    Promise.all([
      loadQuote(sb, quoteId),
      loadClients(sb, tenantId),
      loadRevisions(sb, quoteId),
      quoteNumbers(sb, tenantId),
    ])
      .then(([d, c, r, n]) => {
        setLoaded(d);
        setDraft(d);
        setClients(c);
        setRevisions(r);
        setNumbers(n);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, quoteId, tenantId]);
  useEffect(reload, [reload]);

  const agg = draft?.agg ?? null;
  const issues = useMemo(() => (agg ? validateQuoteForSend(agg) : []), [agg]);
  const numberIssues = useMemo(
    () =>
      agg
        ? validateQuoteNumber(
            agg.quote.number,
            numbers.filter((n) => n !== loaded?.agg.quote.number),
          )
        : [],
    [agg, numbers, loaded],
  );

  if (error) {
    return (
      <section className="page">
        <ErrorState message={error} onRetry={reload} />
        <Button onClick={onBack}>Back to quotes</Button>
      </section>
    );
  }
  if (!draft || !agg || !clients || !loaded) {
    return (
      <section className="page">
        <Loading what="the quote" />
      </section>
    );
  }

  const currentOption = optionsOf(agg).find((o) => o.id === optionId) ?? optionsOf(agg)[0]!;
  // decision 166: office staff edit the working revision of a draft, sent or declined quote; managers send it
  const write = canEditQuote(roles, loaded.agg.quote.status);
  const update = (next: Partial<QuoteAggregate>): void => setDraft({ ...draft, agg: { ...agg, ...next } });
  const setQuote = <K extends keyof Quote>(k: K, v: Quote[K]): void =>
    update({ quote: { ...agg.quote, [k]: v } });
  const dirty = JSON.stringify(draft) !== JSON.stringify(loaded);

  async function act(name: string, action: () => Promise<string | void>): Promise<void> {
    setBusy(name);
    setNotice(null);
    try {
      const done = await action();
      if (done) setNotice({ ok: true, text: done });
    } catch (e: unknown) {
      setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  }

  const save = (): Promise<void> =>
    act('save', async () => {
      if (numberIssues.length) throw new Error(numberIssues[0]!.message);
      const fresh = await saveQuote(sb, draft, loaded, userId);
      setLoaded(fresh);
      setDraft(fresh);
      return `Draft of ${fresh.agg.quote.number} saved.`;
    });

  const stepIssues = (s: Step): ValidationIssue[] => issues.filter((i) => stepOf(i.field) === s);
  const index = STEPS.indexOf(step);

  return (
    <section className="page quote-editor">
      <PageHeader
        title={`Quote ${agg.quote.number}`}
        subtitle={`${agg.client?.name ?? 'No client yet'} · ${agg.quote.project_name || 'No project name'} · revision ${agg.quote.revision} · ${QUOTE_STATUS_LABEL[agg.quote.status]}`}
      >
        <Button variant="quiet" onClick={onBack}>
          Back to quotes
        </Button>
        {write && (
          <Button variant="primary" busy={busy === 'save'} disabled={!dirty} onClick={() => void save()}>
            {busy === 'save' ? 'Saving…' : dirty ? 'Save draft' : 'Saved'}
          </Button>
        )}
      </PageHeader>

      <nav aria-label="Quote steps">
        <ol className="steps">
          {STEPS.map((s, i) => {
            const n = stepIssues(s).length;
            return (
              <li key={s}>
                <button
                  type="button"
                  className={s === step ? 'steps__item steps__item--on' : 'steps__item'}
                  aria-current={s === step ? 'step' : undefined}
                  onClick={() => setStep(s)}
                >
                  <span className="data">{i + 1}</span> {s}
                  {n > 0 && s !== 'Review' && <span className="caption steps__count"> · {n} to fix</span>}
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {!write && (
        <p className="body muted">
          {can(roles, 'quote.draft')
            ? `Read only: the quote is ${QUOTE_STATUS_LABEL[loaded.agg.quote.status].toLowerCase()}; managers and tenant admins change it now.`
            : 'Read only: managers, tenant admins and office staff change quotes.'}
        </p>
      )}
      {write && !send && (
        <p className="body muted">
          You can edit this draft and save it. A manager or tenant admin sends it to the client.
        </p>
      )}

      <fieldset className="fieldset step" disabled={!write} aria-label={step}>
        {step === 'Client' && (
          <ClientStep
            agg={agg}
            clients={clients}
            setQuote={setQuote}
            update={update}
            issues={issues}
            numberIssues={numberIssues}
          />
        )}
        {(step === 'Areas' || step === 'Pricing') && (
          <OptionsBar
            agg={agg}
            current={currentOption.id}
            onSelect={setOptionId}
            update={update}
            issues={issues}
            edit={step === 'Areas'}
          />
        )}
        {step === 'Areas' && <AreasStep agg={agg} optionId={currentOption.id} update={update} />}
        {step === 'Specification' && <SpecStep agg={agg} setQuote={setQuote} update={update} />}
        {step === 'QC limits' && (
          <QcRulesEditor
            rules={agg.qc}
            onChange={(qc) => update({ qc })}
            onReset={() => update({ qc: tenantQcDefaults(docs, agg.quote.aircraft_type) })}
            issues={issues}
          />
        )}
        {step === 'Pricing' && (
          <PricingStep
            agg={agg}
            optionId={currentOption.id}
            update={update}
            setQuote={setQuote}
            docs={docs}
            issues={issues}
          />
        )}
      </fieldset>
      {step === 'Review' && (
        <ReviewStep
          sb={sb}
          tenantId={tenantId}
          userId={userId}
          agg={agg}
          docs={docs}
          issues={issues}
          dirty={dirty}
          send={send}
          revisions={revisions}
          busy={busy}
          act={act}
          onChanged={reload}
          onOpenContract={onOpenContract}
        />
      )}
      {step !== 'Review' && (
        <IssueList title={`To fix in ${step} before sending`} issues={stepIssues(step)} />
      )}

      <div className="row">
        <Button variant="quiet" disabled={index === 0} onClick={() => setStep(STEPS[index - 1]!)}>
          Back
        </Button>
        <Button disabled={index === STEPS.length - 1} onClick={() => setStep(STEPS[index + 1]!)}>
          Next: {STEPS[index + 1] ?? ''}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- 1 client and job

function ClientStep({
  agg,
  clients,
  setQuote,
  update,
  issues,
  numberIssues,
}: {
  readonly agg: QuoteAggregate;
  readonly clients: Clients;
  readonly setQuote: <K extends keyof Quote>(k: K, v: Quote[K]) => void;
  readonly update: (n: Partial<QuoteAggregate>) => void;
  readonly issues: readonly ValidationIssue[];
  readonly numberIssues: readonly ValidationIssue[];
}): ReactElement {
  const q = agg.quote;
  const contacts = clients.contacts.filter((c) => c.client_org_id === q.client_org_id);
  const pickOrg = (id: string): void => {
    const org = clients.orgs.find((o) => o.id === id) ?? null;
    update({
      client: org,
      principal: null,
      notices: null,
      quote: { ...q, client_org_id: org?.id ?? null, principal_contact_id: null, notices_contact_id: null },
    });
  };
  const pickContact = (which: 'principal' | 'notices', id: string): void => {
    const c = contacts.find((x) => x.id === id) ?? null;
    update({
      [which]: c,
      quote: { ...q, [`${which}_contact_id`]: c?.id ?? null },
    } as Partial<QuoteAggregate>);
  };
  const e = (f: string): string | undefined => errorFor(issues, f);
  return (
    <div className="stack">
      <h2 className="heading">Client and job</h2>
      {clients.orgs.length === 0 && (
        <p className="notice">There are no clients yet. Add the client on the Clients page first.</p>
      )}
      <div className="form-grid">
        <SelectField
          label="Client"
          value={q.client_org_id ?? ''}
          onChange={pickOrg}
          error={e('client_org_id') ?? e('client.address')}
          options={[['', 'Choose the client'], ...clients.orgs.map((o) => [o.id, o.name] as const)]}
        />
        <SelectField
          label="Principal client contact"
          value={q.principal_contact_id ?? ''}
          onChange={(v) => pickContact('principal', v)}
          error={e('principal_contact_id')}
          options={[['', 'Choose the contact'], ...contacts.map((c) => [c.id, c.name] as const)]}
        />
        <SelectField
          label="Contact for notices"
          value={q.notices_contact_id ?? ''}
          onChange={(v) => pickContact('notices', v)}
          hint="Leave empty to use the principal contact."
          options={[['', 'Same as the principal contact'], ...contacts.map((c) => [c.id, c.name] as const)]}
        />
        <TextField
          label="Quote number"
          spellCheck={false}
          value={q.number}
          onChange={(v) => setQuote('number', v.toUpperCase())}
          hint="Given by Teslyn; you may type another unused number."
          error={numberIssues[0]?.message}
        />
        <TextField
          label="Quote date"
          type="date"
          value={q.quote_date}
          onChange={(v) => setQuote('quote_date', v)}
        />
        <TextField
          label="Project name"
          value={q.project_name}
          onChange={(v) => setQuote('project_name', v)}
          error={e('project_name')}
        />
        <TextField
          label="Location"
          hint="Printed on the cover, for example Western Australia."
          value={q.location}
          onChange={(v) => setQuote('location', v)}
          error={e('location')}
        />
        <TextField
          label="Located"
          hint="Optional: “The survey is located … in <location>”."
          value={q.located}
          onChange={(v) => setQuote('located', v)}
        />
        <SelectField
          label="State"
          value={q.state}
          onChange={(v) => setQuote('state', v)}
          options={[['', 'Not in Australia'], ...AU_STATES.map((s) => [s.name, s.name] as const)]}
        />
        <TextField
          label="Country"
          value={q.country}
          onChange={(v) => setQuote('country', v)}
          error={e('country')}
        />
        <SelectField
          label="Job type"
          value={q.job_type}
          onChange={(v) => setQuote('job_type', v)}
          options={[
            ['fixed_wing', 'Fixed wing'],
            ['helicopter', 'Helicopter'],
          ]}
        />
        <TextField
          label="Aircraft type offered"
          hint="For example Cessna 208."
          value={q.aircraft_type}
          onChange={(v) => setQuote('aircraft_type', v)}
          error={e('aircraft_type')}
        />
        <NumberField
          label="Cruise speed"
          unit="kt"
          value={q.aircraft_cruise_kt}
          onChange={(v) => setQuote('aircraft_cruise_kt', v)}
          hint="Gives the sample distances in the equipment text."
        />
        <TextField
          label="Proposed bases"
          hint="Separate several with commas."
          value={q.bases.join(', ')}
          onChange={(v) =>
            setQuote(
              'bases',
              v
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          error={e('bases')}
        />
        <SelectField
          label="Estimated start"
          value={q.est_start_qualifier}
          onChange={(v) => setQuote('est_start_qualifier', v)}
          options={[
            ['in', 'In'],
            ['first_week', 'First week of'],
            ['early', 'Early'],
            ['mid', 'Mid'],
            ['late', 'Late'],
            ['q1', 'First quarter of'],
            ['q2', 'Second quarter of'],
            ['q3', 'Third quarter of'],
            ['q4', 'Fourth quarter of'],
          ]}
        />
        <TextField
          label="Start month"
          type="month"
          value={q.est_start_month}
          onChange={(v) => setQuote('est_start_month', v)}
          error={e('est_start_month')}
        />
        <NumberField
          label="Estimated duration"
          value={q.est_duration_value}
          onChange={(v) => setQuote('est_duration_value', v)}
          error={e('est_duration_value')}
        />
        <SelectField
          label="Duration unit"
          value={q.est_duration_unit}
          onChange={(v) => setQuote('est_duration_unit', v)}
          options={[
            ['days', 'Days'],
            ['weeks', 'Weeks'],
            ['months', 'Months'],
          ]}
        />
        <TextField
          label="Duration note"
          value={q.est_duration_note}
          onChange={(v) => setQuote('est_duration_note', v)}
        />
        <NumberField
          label="Delivery"
          unit="working days"
          step="1"
          value={q.delivery_working_days}
          onChange={(v) => setQuote('delivery_working_days', v)}
        />
        <TextField
          label="Governing law state"
          hint="For example Victoria."
          value={q.governing_law}
          onChange={(v) => setQuote('governing_law', v)}
        />
        <TextField
          label="Signed by"
          value={q.signatory_name}
          onChange={(v) => setQuote('signatory_name', v)}
          error={e('signatory_name')}
        />
        <TextField
          label="Signatory title"
          value={q.signatory_title}
          onChange={(v) => setQuote('signatory_title', v)}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- options (decision 171)

/**
 * The quote's options side by side: pick the one to edit, add one (a copy of the one shown, so only what differs is
 * changed), remove one, and in the Areas step name it and give its aircraft.
 */
function OptionsBar({
  agg,
  current,
  onSelect,
  update,
  issues,
  edit,
}: {
  readonly agg: QuoteAggregate;
  readonly current: string;
  readonly onSelect: (id: string) => void;
  readonly update: (n: Partial<QuoteAggregate>) => void;
  readonly issues: readonly ValidationIssue[];
  readonly edit: boolean;
}): ReactElement {
  const options = optionsOf(agg);
  const o = options.find((x) => x.id === current) ?? options[0]!;
  const setOption = (patch: Partial<QuoteOption>): void =>
    update({ options: options.map((x) => (x.id === o.id ? { ...x, ...patch } : x)) });
  const add = (): void => {
    const made = addOption(agg, o.id, newId);
    if (!made) return;
    update(made.agg);
    onSelect(made.option.id);
  };
  const remove = (): void => {
    const next = removeOption(agg, o.id);
    update(next);
    onSelect(optionsOf(next)[0]!.id);
  };
  return (
    <div className="panel stack" aria-label="Quote options">
      <div className="row row--wrap">
        <span className="label muted">Options</span>
        {options.map((x) => (
          <Button
            key={x.id}
            variant={x.id === o.id ? 'primary' : 'quiet'}
            aria-pressed={x.id === o.id}
            onClick={() => onSelect(x.id)}
          >
            {optionTitle(x)}
          </Button>
        ))}
        {edit && (
          <Button variant="quiet" disabled={nextOptionLabel(options) === null} onClick={add}>
            Add option (copy of {o.label})
          </Button>
        )}
        {edit && options.length > 1 && (
          <Button variant="quiet" onClick={remove} aria-label={`Remove option ${o.label}`}>
            Remove option {o.label}
          </Button>
        )}
      </div>
      {options.length === 1 && (
        <p className="caption muted">
          One option. Add an option to offer the client alternatives side by side, for example another block
          size or aircraft type (up to {MAX_QUOTE_OPTIONS}).
        </p>
      )}
      {edit && options.length > 1 && (
        <div className="form-grid">
          <TextField
            label="Option label"
            value={o.label}
            onChange={(v) => setOption({ label: v.toUpperCase().slice(0, 3) })}
            error={errorFor(issues, `option.${o.id}.label`)}
          />
          <TextField
            label="What this option offers"
            hint="For example: Milgun block only, or 50 m line spacing."
            value={o.description}
            onChange={(v) => setOption({ description: v })}
          />
          <TextField
            label="Aircraft type of this option"
            hint={`Leave empty for the quote's ${agg.quote.aircraft_type || 'aircraft type'}.`}
            value={o.aircraft_type}
            onChange={(v) => setOption({ aircraft_type: v })}
          />
          <NumberField
            label="Cruise speed of this option"
            unit="kt"
            value={o.aircraft_cruise_kt}
            onChange={(v) => setOption({ aircraft_cruise_kt: v })}
          />
        </div>
      )}
      {errorFor(issues, `option.${o.id}.areas`) && (
        <p className="field__error caption">{errorFor(issues, `option.${o.id}.areas`)}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- 2 areas

/** One area's flight-plan files: the lines file (`.lns` or `.sum`), its `_BND.inp` and the client's `_BND.kml`. */
interface PlanFileSet {
  readonly lines: File;
  readonly bnd: File | null;
  readonly kml: File | null;
  readonly name: string;
}

function AreasStep({
  agg,
  optionId,
  update,
}: {
  readonly agg: QuoteAggregate;
  readonly optionId: string;
  readonly update: (n: Partial<QuoteAggregate>) => void;
}): ReactElement {
  const [fileError, setFileError] = useState<string | null>(null);
  const option = optionsOf(agg).find((o) => o.id === optionId)!;
  const areas = optionAreas(agg, optionId).sort((a, b) => a.sort - b.sort);
  const setArea = (id: string, patch: Partial<QuoteArea>): void =>
    update({ areas: agg.areas.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
  const nextSort = (): number => areas.reduce((m, a) => Math.max(m, a.sort), 0) + 1;

  /** Areas from flight-plan file sets; `byBlock` orders them by the block digit of their line numbers. */
  async function addAreas(sets: readonly PlanFileSet[], byBlock: boolean): Promise<boolean> {
    const parsed: Array<{ readonly set: PlanFileSet; readonly lines: LinesFile }> = [];
    const added: QuoteArea[] = [];
    try {
      for (const set of sets) {
        const text = await set.lines.text();
        const lines = /\.lns$/i.test(set.lines.name) ? parseLnsFile(text) : parseSumFile(text);
        parsed.push({ set, lines });
      }
      const digit = (l: LinesFile): string => String(l.lines[0]?.number ?? '')[0] ?? '';
      if (byBlock)
        parsed.sort((a, b) =>
          digit(a.lines) < digit(b.lines) ? -1 : digit(a.lines) > digit(b.lines) ? 1 : 0,
        );
      for (const [i, { set, lines }] of parsed.entries()) {
        const bnd = set.bnd ? parseBndFile(await set.bnd.text()) : null;
        const kml = set.kml ? parseBoundaryKml(await set.kml.text()) : null;
        const area = areaFromPlanFiles(
          {
            id: newId(),
            quote_id: agg.quote.id,
            option_id: optionId,
            sort: nextSort() + i,
            name: set.name,
            sensor_height_m: 0,
            rate_per_line_km: null,
          },
          lines,
          bnd,
          set.lines.name,
        );
        added.push({
          ...area,
          boundary_table:
            kml && set.kml
              ? boundaryTableFromKml(kml, set.kml.name)
              : bnd && set.bnd
                ? boundaryTableFromBnd(
                    { ...bnd, zone: bnd.zone ?? area.zone },
                    agg.quote.coords_datum || 'WGS84',
                    set.bnd.name,
                  )
                : null,
        });
      }
    } catch (e: unknown) {
      setFileError(e instanceof Error ? e.message : String(e));
      return false;
    }
    update({ areas: [...agg.areas, ...added] });
    return true;
  }

  async function importFiles(files: FileList | null): Promise<void> {
    setFileError(null);
    if (!files || files.length === 0) return;
    const list = [...files];
    // decision 169: the km come from each block's lines file (.lns); a .sum holds the same line list
    const stemOf = (name: string): string => name.replace(/(_LNS)?\.(lns|sum)$/i, '');
    const linesFiles = list.filter((f) => /\.(lns|sum)$/i.test(f.name));
    const byStem = new Map<string, File>();
    for (const f of linesFiles) {
      const key = stemOf(f.name).toLowerCase();
      const had = byStem.get(key);
      if (!had || /\.lns$/i.test(f.name)) byStem.set(key, f);
    }
    if (byStem.size === 0) {
      setFileError(
        "Choose each area's lines file (.lns) or flight-plan summary (.sum), with its boundary (_BND.inp) if you have it.",
      );
      return;
    }
    const sets = [...byStem.values()].map((s): PlanFileSet => {
      const stem = stemOf(s.name).toLowerCase();
      return {
        lines: s,
        bnd: list.find((f) => /_BND\.inp$/i.test(f.name) && f.name.toLowerCase().startsWith(stem)) ?? null,
        // decision 170: the client's boundary KML is printed as supplied (longitude/latitude, WGS 84)
        kml: list.find((f) => /_BND\.kml$/i.test(f.name) && f.name.toLowerCase().startsWith(stem)) ?? null,
        name: blockNameOf(s.name),
      };
    });
    await addAreas(sets, false);
  }

  /**
   * Decision 189: each block's flight plan is in `<project folder>\<job>_<block>\1. Flight Plan\`; its lines file is
   * the `_LNS.lns` there (the `_fnl.sum` only when there is no `.lns`), its boundary the `_BND.inp`. The boundary
   * table is the `_BND.inp`'s grid coordinates, as the issued contracts print them.
   */
  async function importFolder(files: FileList | null): Promise<void> {
    setFileError(null);
    if (!files || files.length === 0) return;
    const blocks = findBlockPlanFiles([...files], (f) => f.webkitRelativePath || f.name);
    if (blocks.length === 0) {
      setFileError(
        `No flight plan in that folder. Choose the project folder or a block folder: each block's files are in <job>_<block>\\${FLIGHT_PLAN_DIR}\\.`,
      );
      return;
    }
    const sets = blocks.flatMap((b): PlanFileSet[] =>
      b.lines ? [{ lines: b.lines, bnd: b.bnd, kml: null, name: blockNameOf(b.folder || b.lines.name) }] : [],
    );
    const missing = blocks.filter((b) => !b.lines).map((b) => `${b.folder}\\${FLIGHT_PLAN_DIR}`);
    const ok = await addAreas(sets, true);
    if (ok && missing.length > 0) {
      setFileError(`No lines file (_LNS.lns) or flight-plan summary (_fnl.sum) in ${missing.join(', ')}.`);
    }
  }

  const addByHand = (): void =>
    update({
      areas: [
        ...agg.areas,
        {
          id: newId(),
          quote_id: agg.quote.id,
          option_id: optionId,
          sort: nextSort(),
          name: '',
          trav_spacing_m: 100,
          trav_direction_deg: 0,
          tie_spacing_m: 1000,
          tie_direction_deg: 90,
          sensor_height_m: 0,
          zone: null,
          hemisphere: 'S',
          traverse_km: 0,
          tie_km: 0,
          line_km_override: null,
          override_reason: '',
          rate_per_line_km: null,
          boundary: [],
          boundary_table: null,
          lines_file: '',
        },
      ],
    });

  const check = lineKmCheck(areas, option.stated_total_line_km);
  const setStated = (v: number | null): void =>
    update({
      options: optionsOf(agg).map((o) => (o.id === optionId ? { ...o, stated_total_line_km: v } : o)),
    });
  const flagFor = (id: string): string | undefined =>
    check.flags.find((f) => f.field.startsWith(`area.${id}.`))?.message;
  return (
    <div className="stack">
      <h2 className="heading">Survey areas</h2>
      <p className="body muted">
        Import the project folder (or one block folder): Teslyn finds each block's lines file in its &quot;1.
        Flight Plan&quot; folder (the _LNS.lns, or the .sum when there is no .lns) and its boundary
        (_BND.inp). Or import each area's lines file (.lns) or flight-plan summary (.sum) with its boundary
        (_BND.inp, and the client's _BND.kml if the contract should print longitude and latitude). The line km
        are always computed from the lines file: traverse and tie km are each rounded to whole km and added.
        The total is the sum of the areas.
      </p>
      <div className="row row--wrap">
        <label className="btn btn--secondary file-pick">
          Import a project or block folder
          <input
            type="file"
            multiple
            className="sr-only"
            ref={(el) => {
              el?.setAttribute('webkitdirectory', '');
            }}
            onChange={(e) => void importFolder(e.target.files)}
          />
        </label>
        <label className="btn btn--secondary file-pick">
          Import flight plan files
          <input
            type="file"
            multiple
            accept=".lns,.sum,.inp,.kml"
            className="sr-only"
            onChange={(e) => void importFiles(e.target.files)}
          />
        </label>
        <Button variant="quiet" onClick={addByHand}>
          Add an area by hand
        </Button>
      </div>
      {fileError && (
        <p className="notice notice--fail" role="alert">
          {fileError}
        </p>
      )}
      {areas.length === 0 && <p className="body">No areas yet.</p>}
      {areas.map((a) => {
        const issues = validateArea(a, false);
        const e = (k: string): string | undefined => errorFor(issues, `area.${a.id}.${k}`);
        return (
          <article key={a.id} className="panel stack" aria-label={`Area ${a.name || 'without a name'}`}>
            <div className="form-grid">
              <TextField
                label="Area name"
                value={a.name}
                onChange={(v) => setArea(a.id, { name: v })}
                error={e('name')}
              />
              <NumberField
                label="Traverse spacing"
                unit="m"
                value={a.trav_spacing_m}
                onChange={(v) => setArea(a.id, { trav_spacing_m: v ?? 0 })}
                error={e('trav_spacing_m')}
              />
              <NumberField
                label="Traverse direction"
                unit="°"
                value={a.trav_direction_deg}
                onChange={(v) => setArea(a.id, { trav_direction_deg: v ?? 0 })}
              />
              <NumberField
                label="Tie spacing"
                unit="m"
                value={a.tie_spacing_m}
                onChange={(v) => setArea(a.id, { tie_spacing_m: v ?? 0 })}
                error={e('tie_spacing_m')}
              />
              <NumberField
                label="Tie direction"
                unit="°"
                value={a.tie_direction_deg}
                onChange={(v) => setArea(a.id, { tie_direction_deg: v ?? 0 })}
              />
              <NumberField
                label="Survey (sensor) height"
                unit="m"
                value={a.sensor_height_m}
                onChange={(v) => setArea(a.id, { sensor_height_m: v ?? 0 })}
                error={e('sensor_height_m')}
              />
              <NumberField
                label="UTM zone"
                step="1"
                value={a.zone}
                onChange={(v) => setArea(a.id, { zone: v })}
                error={e('zone')}
              />
              <SelectField
                label="Hemisphere"
                value={a.hemisphere}
                onChange={(v) => setArea(a.id, { hemisphere: v })}
                options={[
                  ['S', 'South'],
                  ['N', 'North'],
                ]}
              />
              <NumberField
                label="Line km typed or imported"
                step="1"
                value={a.line_km_override}
                onChange={(v) => setArea(a.id, { line_km_override: v })}
                hint="With a lines file this figure is only compared; without one it is quoted."
              />
              {a.line_km_override !== null && (
                <TextField
                  label="Where the typed km come from"
                  value={a.override_reason}
                  onChange={(v) => setArea(a.id, { override_reason: v })}
                  error={e('override_reason')}
                />
              )}
            </div>
            <p className="body">
              <span className="data">{formatKmInt(a.traverse_km)}</span> traverse km +{' '}
              <span className="data">{formatKmInt(a.tie_km)}</span> tie km from{' '}
              {a.lines_file || 'no lines file'} · quoted{' '}
              <strong className="data">{formatKmInt(areaLineKm(a))} line km</strong> · boundary{' '}
              {a.boundary.length} points
            </p>
            {a.boundary_table && (
              <div className="row row--wrap">
                <p className="body">
                  Boundary table: <span className="data">{a.boundary_table.points.length}</span> points,{' '}
                  {a.boundary_table.kind === 'grid'
                    ? `easting and northing, zone ${a.boundary_table.zone ?? 'not set'}`
                    : 'longitude and latitude'}
                  {a.boundary_table.source ? `, from ${a.boundary_table.source}` : ''}
                </p>
                <SelectField
                  label="Datum of the boundary table"
                  value={a.boundary_table.datum}
                  onChange={(v) => setArea(a.id, { boundary_table: { ...a.boundary_table!, datum: v } })}
                  hint="Printed with the coordinates; it may differ from the survey datum."
                  error={e('boundary_table.datum')}
                  options={BOUNDARY_DATUMS}
                />
              </div>
            )}
            {e('lines_file') && <p className="field__error caption">{e('lines_file')}</p>}
            {flagFor(a.id) && (
              <p className="notice notice--warn caption" role="status">
                <strong>Check:</strong> {flagFor(a.id)}
              </p>
            )}
            <div className="row">
              <Button
                variant="quiet"
                onClick={() => update({ areas: agg.areas.filter((x) => x.id !== a.id) })}
                aria-label={`Remove area ${a.name}`}
              >
                Remove area
              </Button>
            </div>
          </article>
        );
      })}
      {areas.length > 0 && (
        <>
          <p className="heading">
            Total <span className="data">{formatKmInt(check.total_km)}</span> line km
          </p>
          <div className="form-grid">
            <NumberField
              label="Total stated on an imported quote or contract"
              unit="line km"
              step="1"
              value={option.stated_total_line_km}
              onChange={setStated}
              hint="Optional, for comparison only: the quote prints the sum of the areas."
            />
          </div>
          {check.flags.some((f) => f.field === 'stated_total_line_km') && (
            <p className="notice notice--warn caption" role="status">
              <strong>Check:</strong> {check.flags.find((f) => f.field === 'stated_total_line_km')!.message}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** The line-km check (decision 169) in words, for every option: what differs from the lines files. */
export function LineKmFlags({ agg }: { readonly agg: QuoteAggregate }): ReactElement | null {
  const options = optionsOf(agg);
  const flags = options.flatMap((o) =>
    lineKmCheck(optionAreas(agg, o.id), o.stated_total_line_km).flags.map((f) => ({
      key: `${o.id}:${f.field}`,
      message: options.length > 1 ? `${optionName(o)}: ${f.message}` : f.message,
    })),
  );
  if (flags.length === 0) return null;
  return (
    <div className="notice notice--warn" role="status" aria-label="Line km check">
      <p className="label">Line km check: {flags.length} to look at (the quote uses the lines files)</p>
      <ul className="issues">
        {flags.map((f) => (
          <li key={f.key} className="body">
            {f.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------- 3 specification

function SpecStep({
  agg,
  setQuote,
  update,
}: {
  readonly agg: QuoteAggregate;
  readonly setQuote: <K extends keyof Quote>(k: K, v: Quote[K]) => void;
  readonly update: (n: Partial<QuoteAggregate>) => void;
}): ReactElement {
  const s = agg.sampling;
  const setS = <K extends keyof typeof s>(k: K, v: (typeof s)[K]): void =>
    update({ sampling: { ...s, [k]: v } });
  const q = agg.quote;
  const deliverables = [...agg.deliverables].sort((a, b) => a.sort - b.sort);
  const setD = (id: string, patch: Partial<DeliverableSpec>): void =>
    update({ deliverables: agg.deliverables.map((d) => (d.id === id ? { ...d, ...patch } : d)) });
  const addD = (kind: DeliverableSpec['kind'], method: DeliverableSpec['method'], items: string[]): void => {
    const n = deliverables.filter((d) => d.kind === kind).length + 1;
    update({
      deliverables: [
        ...agg.deliverables,
        {
          id: newId(),
          quote_id: q.id,
          sort: deliverables.length + 1,
          kind,
          label: kind === 'located' ? `Located Data File #${n}` : `Grids #${n}`,
          contents: '',
          method,
          items,
        },
      ],
    });
  };
  const formats = (list: readonly string[]): ReadonlyArray<readonly [string, string]> =>
    list.map((f) => [f, f || "Client's preferred format"] as const);
  return (
    <div className="stack">
      <h2 className="heading">Sampling</h2>
      <div className="form-grid">
        <CheckField
          label="Magnetics"
          checked={s.mag_rate_hz !== null}
          onChange={(on) => setS('mag_rate_hz', on ? 20 : null)}
        />
        {s.mag_rate_hz !== null && (
          <>
            <SelectField
              label="Mag sample rate"
              value={String(s.mag_rate_hz)}
              onChange={(v) => setS('mag_rate_hz', Number(v))}
              options={[
                ['20', '20 Hz'],
                ['40', '40 Hz'],
              ]}
            />
            <NumberField
              label="Magnetometers"
              step="1"
              value={s.magnetometers}
              onChange={(v) => setS('magnetometers', v ?? 0)}
            />
            <SelectField
              label="Mag located format"
              value={s.mag_located_format}
              onChange={(v) => setS('mag_located_format', v)}
              options={formats(LOCATED_FORMATS)}
            />
            <SelectField
              label="Mag grid format"
              value={s.mag_grid_format}
              onChange={(v) => setS('mag_grid_format', v)}
              options={formats(GRID_FORMATS)}
            />
          </>
        )}
        <CheckField
          label="Radiometrics"
          checked={s.rad_rate_hz !== null}
          onChange={(on) => setS('rad_rate_hz', on ? 2 : null)}
        />
        {s.rad_rate_hz !== null && (
          <>
            <SelectField
              label="Rad sample rate"
              value={String(s.rad_rate_hz)}
              onChange={(v) => setS('rad_rate_hz', Number(v))}
              options={[
                ['0.5', '0.5 Hz'],
                ['1', '1 Hz'],
                ['2', '2 Hz'],
              ]}
            />
            <SelectField
              label="Detector packs"
              value={String(s.spectrometer_packs)}
              onChange={(v) => setS('spectrometer_packs', Number(v))}
              options={['0', '1', '2', '3', '4'].map((n) => [n, n] as const)}
            />
            <CheckField
              label="Include 4.2 litre upward detector"
              checked={s.upward_detector}
              onChange={(v) => setS('upward_detector', v)}
            />
            <CheckField
              label="Convert to ground concentrations"
              checked={s.ground_concentrations}
              onChange={(v) => setS('ground_concentrations', v)}
            />
            <SelectField
              label="Rad located format"
              value={s.rad_located_format}
              onChange={(v) => setS('rad_located_format', v)}
              options={formats(LOCATED_FORMATS)}
            />
            <SelectField
              label="Rad grid format"
              value={s.rad_grid_format}
              onChange={(v) => setS('rad_grid_format', v)}
              options={formats(GRID_FORMATS)}
            />
          </>
        )}
        <SelectField
          label="Base station"
          value={s.base_station}
          onChange={(v) => setS('base_station', v)}
          options={BASE_STATIONS.map((b) => [b, b] as const)}
        />
        <SelectField
          label="Survey datum"
          value={s.datum}
          onChange={(v) => setS('datum', v)}
          options={[
            ['GDA94', 'GDA 94'],
            ['GDA2020', 'GDA2020'],
            ['WGS84', 'WGS 84'],
          ]}
        />
        <SelectField
          label="Datum for new boundary tables"
          hint="Each area's boundary table keeps its own datum (Areas step)."
          value={q.coords_datum}
          onChange={(v) => setQuote('coords_datum', v)}
          options={[
            ['WGS84', 'WGS 84'],
            ['GDA94', 'GDA 94'],
            ['GDA2020', 'GDA2020'],
          ]}
        />
        <CheckField
          label="Forward facing video camera"
          checked={s.video}
          onChange={(v) => setS('video', v)}
        />
      </div>

      <h2 className="heading">Deliverables</h2>
      <p className="body muted">One item per line, in the order they are printed.</p>
      {deliverables.map((d) => (
        <article key={d.id} className="panel stack" aria-label={d.label}>
          <div className="form-grid">
            <TextField label="Title" value={d.label} onChange={(v) => setD(d.id, { label: v })} />
            {d.kind === 'located' && (
              <TextField
                label="Contents"
                hint="For example: contains final magnetic, altitude and ancillary data"
                value={d.contents}
                onChange={(v) => setD(d.id, { contents: v })}
              />
            )}
            <SelectField
              label="Method"
              value={d.method}
              onChange={(v) => setD(d.id, { method: v })}
              options={[
                ['mag', 'Magnetics'],
                ['rad', 'Radiometrics'],
                ['none', 'No field list (spectra)'],
              ]}
            />
          </div>
          <TextArea
            label="Items"
            rows={6}
            value={d.items.join('\n')}
            onChange={(v) =>
              setD(d.id, {
                items: v
                  .split('\n')
                  .map((l) => l.trim())
                  .filter(Boolean),
              })
            }
          />
          <div className="row">
            <Button
              variant="quiet"
              onClick={() => update({ deliverables: agg.deliverables.filter((x) => x.id !== d.id) })}
              aria-label={`Remove ${d.label}`}
            >
              Remove
            </Button>
          </div>
        </article>
      ))}
      <div className="row row--wrap">
        <Button variant="quiet" onClick={() => addD('located', 'mag', defaultChannelSet(0))}>
          Add magnetic located file
        </Button>
        <Button variant="quiet" onClick={() => addD('located', 'rad', defaultChannelSet(1))}>
          Add radiometric located file
        </Button>
        <Button variant="quiet" onClick={() => addD('grid', 'mag', defaultChannelSet(3))}>
          Add grid list
        </Button>
      </div>

      <h2 className="heading">Conduct of survey</h2>
      <fieldset className="fieldset stack">
        <legend className="label muted">The company provides</legend>
        {CONDUCT_ITEMS.map((c) => (
          <CheckField
            key={c}
            label={CONDUCT_LABEL[c]}
            checked={q.conduct_items.includes(c)}
            onChange={(on) =>
              setQuote('conduct_items', on ? [...q.conduct_items, c] : q.conduct_items.filter((x) => x !== c))
            }
          />
        ))}
      </fieldset>
      <div className="form-grid">
        <SelectField
          label="Aircraft fuel"
          value={q.fuel_by}
          onChange={(v) => setQuote('fuel_by', v)}
          options={[
            ['not_stated', 'Not stated'],
            ['thomson', 'We provide and pay for it'],
            ['client', 'The client pays for it'],
          ]}
        />
        <SelectField
          label="Permits"
          value={q.permits_by}
          onChange={(v) => setQuote('permits_by', v)}
          options={[
            ['not_stated', 'Not stated'],
            ['thomson_all', 'We obtain all permits'],
            ['thomson_except_tasp', 'All except the TASP (client)'],
          ]}
        />
        <CheckField
          label="Offer free gridded data and GeoTiffs"
          checked={q.free_grid_offer}
          onChange={(v) => setQuote('free_grid_offer', v)}
        />
        <CheckField
          label="Print boundary coordinates"
          checked={q.print_boundary_coords}
          onChange={(v) => setQuote('print_boundary_coords', v)}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- 4 QC limits (also used for contract variations)

export function QcRulesEditor({
  rules,
  onChange,
  onReset,
  issues,
}: {
  readonly rules: readonly QcRule[];
  readonly onChange: (r: QcRule[]) => void;
  readonly onReset?: () => void;
  readonly issues: readonly ValidationIssue[];
}): ReactElement {
  const set = (key: QcRule['key'], patch: Partial<QcRule>): void =>
    onChange(rules.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  return (
    <div className="stack">
      <h2 className="heading">QC limits (reflight specifications)</h2>
      <p className="body muted">
        Printed in the contract's reflight specifications and used by Field's checks. After the contract is
        signed they change only through a variation with a reason.
      </p>
      <div className="table-wrap">
        <table className="table qc-table">
          <thead>
            <tr>
              <th className="label">Rule</th>
              <th className="label">On</th>
              <th className="label">Limit</th>
              <th className="label">Over (m)</th>
              <th className="label">Time (min)</th>
              <th className="label">Sensor</th>
            </tr>
          </thead>
          <tbody>
            {QC_RULE_DEFS.map((d) => {
              const r = rules.find((x) => x.key === d.key);
              if (!r) return null;
              const err = errorFor(issues, `qc.${d.key}`);
              return (
                <tr key={d.key}>
                  <td>
                    {d.label}
                    {!d.printed && <span className="caption muted"> (not printed)</span>}
                    {err && <p className="field__error caption">{err}</p>}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`${d.label} on`}
                      checked={r.enabled}
                      onChange={(e) => set(d.key, { enabled: e.target.checked })}
                    />
                  </td>
                  <td>
                    <input
                      className="input input--inline data"
                      type="number"
                      step="any"
                      aria-label={`${d.label} limit (${d.unit})`}
                      value={Number.isFinite(r.limit) ? r.limit : ''}
                      onChange={(e) =>
                        set(d.key, { limit: e.target.value === '' ? Number.NaN : Number(e.target.value) })
                      }
                    />{' '}
                    <span className="caption muted">{d.unit}</span>
                  </td>
                  <td>
                    {d.over && (
                      <input
                        className="input input--inline data"
                        type="number"
                        aria-label={`${d.label} distance (m)`}
                        value={r.over_m ?? ''}
                        onChange={(e) =>
                          set(d.key, { over_m: e.target.value === '' ? null : Number(e.target.value) })
                        }
                      />
                    )}
                  </td>
                  <td>
                    {d.window && (
                      <input
                        className="input input--inline data"
                        type="number"
                        aria-label={`${d.label} time (min)`}
                        value={r.window_min ?? ''}
                        onChange={(e) =>
                          set(d.key, { window_min: e.target.value === '' ? null : Number(e.target.value) })
                        }
                      />
                    )}
                  </td>
                  <td>
                    {d.key === 'mag_noise_4th_diff' && (
                      <select
                        className="input input--inline"
                        aria-label="Noise sensor"
                        value={r.sensor ?? ''}
                        onChange={(e) => set(d.key, { sensor: e.target.value || null })}
                      >
                        <option value="">All sensors</option>
                        <option value="tail">Tail sensor</option>
                      </select>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {onReset && (
        <div className="row">
          <Button variant="quiet" onClick={onReset}>
            Use the company's defaults
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- 5 pricing

function PricingStep({
  agg,
  optionId,
  update,
  setQuote,
  docs,
  issues,
}: {
  readonly agg: QuoteAggregate;
  /** The option whose rates and charges are shown (decision 171: each option has its own). */
  readonly optionId: string;
  readonly update: (n: Partial<QuoteAggregate>) => void;
  readonly setQuote: <K extends keyof Quote>(k: K, v: Quote[K]) => void;
  readonly docs: TenantDocuments;
  readonly issues: readonly ValidationIssue[];
}): ReactElement {
  const oa = optionAggregate(agg, optionId);
  const mine = (c: QuoteCharge): boolean => oa.charges.includes(c);
  const charge = (k: ChargeKind): QuoteCharge => oa.charges.find((c) => c.kind === k)!;
  const setCharge = (k: ChargeKind, patch: Partial<QuoteCharge>): void =>
    update({ charges: agg.charges.map((c) => (mine(c) && c.kind === k ? { ...c, ...patch } : c)) });
  const t = agg.terms;
  const setT = <K extends keyof typeof t>(k: K, v: (typeof t)[K]): void =>
    update({ terms: { ...t, [k]: v } });
  const totals = quoteTotals(oa, docs.profile);
  const cur = agg.quote.currency;
  const m = (c: number | null): string => (c === null ? '–' : formatMoney(c, cur));
  const e = (f: string): string | undefined => errorFor(issues, f);
  const areas = [...oa.areas].sort((a, b) => a.sort - b.sort);
  return (
    <div className="stack">
      <h2 className="heading">Pricing</h2>
      <div className="form-grid">
        <SelectField
          label="Currency"
          value={cur}
          onChange={(v) => setQuote('currency', v)}
          options={(['AUD', 'USD', 'NZD'] as const).map(
            (c) => [c, `${CURRENCY_LABEL[c].name} dollars (${CURRENCY_LABEL[c].symbol})`] as const,
          )}
        />
      </div>
      <table className="table">
        <thead>
          <tr>
            <th className="label">Area</th>
            <th className="label">Line km</th>
            <th className="label">Rate per line km</th>
            <th className="label">Price</th>
          </tr>
        </thead>
        <tbody>
          {areas.map((a, i) => (
            <tr key={a.id}>
              <td>{a.name}</td>
              <td className="data">{formatKmInt(areaLineKm(a))}</td>
              <td>
                <input
                  className="input input--inline data"
                  type="number"
                  step="0.01"
                  aria-label={`Rate per line km for ${a.name}`}
                  value={a.rate_per_line_km ?? ''}
                  onChange={(ev) =>
                    update({
                      areas: agg.areas.map((x) =>
                        x.id === a.id
                          ? {
                              ...x,
                              rate_per_line_km: ev.target.value === '' ? null : Number(ev.target.value),
                            }
                          : x,
                      ),
                    })
                  }
                />
                {e(`area.${a.id}.rate_per_line_km`) && (
                  <p className="field__error caption">{e(`area.${a.id}.rate_per_line_km`)}</p>
                )}
              </td>
              <td className="data">{m(totals.areas[i]?.price_cents ?? null)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="form-grid">
        <NumberField
          label="Mobilisation and demobilisation"
          unit={CURRENCY_LABEL[cur].symbol}
          step="0.01"
          value={charge('mobilisation').amount}
          onChange={(v) => setCharge('mobilisation', { amount: v })}
          error={e('charge.mobilisation')}
        />
        <NumberField
          label="Data merge"
          unit={CURRENCY_LABEL[cur].symbol}
          step="0.01"
          value={charge('data_merge').amount}
          onChange={(v) => setCharge('data_merge', { amount: v })}
          hint="Optional item: merging this survey with the client's existing survey data. Not in the total."
        />
        <NumberField
          label="TASP estimate"
          unit={CURRENCY_LABEL[cur].symbol}
          step="0.01"
          value={charge('tasp').amount}
          onChange={(v) => setCharge('tasp', { amount: v })}
          hint="Paid by the client to the authority; not in the total."
        />
        <NumberField
          label="Daily standby rate"
          unit={`${CURRENCY_LABEL[cur].symbol}/day`}
          step="0.01"
          value={charge('standby').amount}
          onChange={(v) => setCharge('standby', { amount: v })}
          error={e('charge.standby')}
        />
        <NumberField
          label="Cap standby"
          unit="days"
          step="1"
          value={charge('standby').cap_days}
          onChange={(v) => setCharge('standby', { cap_days: v })}
          error={e('charge.standby.cap_days')}
        />
        <TextField
          label="Extra standby note"
          value={charge('standby').note}
          onChange={(v) => setCharge('standby', { note: v })}
        />
        <SelectField
          label="Fuel cost"
          value={charge('fuel').fuel_mode}
          onChange={(v) => setCharge('fuel', { fuel_mode: v })}
          options={[
            ['none', 'None'],
            ['at_cost', 'At cost'],
            ['per_litre', 'Per litre'],
          ]}
        />
        {charge('fuel').fuel_mode === 'per_litre' && (
          <NumberField
            label="Fuel"
            unit={`${CURRENCY_LABEL[cur].symbol}/L`}
            step="0.01"
            value={charge('fuel').amount}
            onChange={(v) => setCharge('fuel', { amount: v })}
            error={e('charge.fuel')}
          />
        )}
      </div>
      <h3 className="heading">Schedule of payments</h3>
      <div className="form-grid">
        <NumberField
          label="First payment, on signing"
          unit="%"
          step="1"
          value={t.pp1_pct}
          onChange={(v) => setT('pp1_pct', v)}
          error={e('pp1_pct')}
        />
        <NumberField
          label="Second payment, after acquisition"
          unit="%"
          step="1"
          value={t.pp2_pct}
          onChange={(v) => setT('pp2_pct', v)}
          error={e('pp2_pct')}
        />
        <NumberField
          label="Balance, on delivery"
          unit="%"
          step="1"
          value={t.balance_pct}
          onChange={(v) => setT('balance_pct', v)}
          hint="Any part may be 0 %; the three must add up to 100 %."
          error={e('balance_pct')}
        />
        <NumberField
          label="Invoices due within"
          unit="days"
          step="1"
          value={t.payment_days}
          onChange={(v) => setT('payment_days', v ?? 0)}
        />
        <NumberField
          label="Late payment penalty"
          unit="% per month"
          step="0.1"
          value={t.late_fee_pct_month}
          onChange={(v) => setT('late_fee_pct_month', v ?? 0)}
        />
      </div>
      <div className="row">
        <Button
          variant="quiet"
          onClick={() => setT('balance_pct', paymentBalance(t.pp1_pct, t.pp2_pct))}
          disabled={t.pp1_pct === null}
        >
          Set the balance to the rest
        </Button>
      </div>
      <Totals agg={oa} docs={docs} />
      {hasOptions(agg) && <OptionsTable agg={agg} docs={docs} />}
    </div>
  );
}

/** The options side by side with their line km and totals (decision 171). */
export function OptionsTable({
  agg,
  docs,
}: {
  readonly agg: QuoteAggregate;
  readonly docs: TenantDocuments;
}): ReactElement {
  const m = (c: number | null): string => (c === null ? 'not complete' : formatMoney(c, agg.quote.currency));
  return (
    <table className="table" aria-label="Options side by side">
      <thead>
        <tr>
          <th className="label">Option</th>
          <th className="label">Aircraft</th>
          <th className="label">Areas</th>
          <th className="label">Line km</th>
          <th className="label">Estimated total excluding standby</th>
          <th className="label">Daily standby</th>
        </tr>
      </thead>
      <tbody>
        {optionTotals(agg, docs.profile).map(({ option, agg: oa, totals }) => (
          <tr key={option.id}>
            <td>{optionTitle(option)}</td>
            <td>{oa.quote.aircraft_type || '–'}</td>
            <td className="data">{oa.areas.length}</td>
            <td className="data">{formatKmInt(totals.total_line_km)}</td>
            <td className="data">{m(totals.total_ex_tax_cents)}</td>
            <td className="data">{m(totals.standby_per_day_cents)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Totals({
  agg,
  docs,
}: {
  readonly agg: QuoteAggregate;
  readonly docs: TenantDocuments;
}): ReactElement {
  const totals = quoteTotals(agg, docs.profile);
  const m = (c: number | null): string => (c === null ? 'not complete' : formatMoney(c, agg.quote.currency));
  return (
    <dl className="totals panel">
      <dt className="label">Total line km</dt>
      <dd className="data">{formatKmInt(totals.total_line_km)}</dd>
      <dt className="label">Mobilisation and demobilisation</dt>
      <dd className="data">{m(totals.mobilisation_cents)}</dd>
      <dt className="label">Data acquisition and processing</dt>
      <dd className="data">{m(totals.acquisition_cents)}</dd>
      <dt className="label">
        Estimated total excluding standby
        {docs.profile?.tax_line_enabled ? '' : `, ex ${docs.profile?.tax_label ?? 'GST'}`}
      </dt>
      <dd className="data">{m(totals.total_ex_tax_cents)}</dd>
      {docs.profile?.tax_line_enabled && (
        <>
          <dt className="label">{docs.profile.tax_label}</dt>
          <dd className="data">{m(totals.tax_cents)}</dd>
          <dt className="label">Total including {docs.profile.tax_label}</dt>
          <dd className="data">{m(totals.total_inc_tax_cents)}</dd>
        </>
      )}
      {totals.data_merge_cents !== null && (
        <>
          <dt className="label">Optional data merge, not in the total</dt>
          <dd className="data">{m(totals.data_merge_cents)}</dd>
        </>
      )}
    </dl>
  );
}

// ---------------------------------------------------------------- 6 review, revisions, contract

function ReviewStep({
  sb,
  tenantId,
  userId,
  agg,
  docs,
  issues,
  dirty,
  send,
  revisions,
  busy,
  act,
  onChanged,
  onOpenContract,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly agg: QuoteAggregate;
  readonly docs: TenantDocuments;
  readonly issues: readonly ValidationIssue[];
  readonly dirty: boolean;
  /** Send, accept, decline and make contracts (managers and tenant admins). */
  readonly send: boolean;
  readonly revisions: readonly QuoteRevision[];
  readonly busy: string | null;
  readonly act: (name: string, action: () => Promise<string | void>) => Promise<void>;
  readonly onChanged: () => void;
  readonly onOpenContract: (contractId: string) => void;
}): ReactElement {
  const quoteClauses = activeClauses(docs, 'quote');
  const contractClauses = activeClauses(docs, 'contract');
  const [contractFor, setContractFor] = useState<QuoteRevision | null>(null);
  const [acceptFor, setAcceptFor] = useState<QuoteRevision | null>(null);

  const pdf = (snapshot: QuoteAggregate, name: string): Promise<void> =>
    act(name, async () => {
      if (!quoteClauses)
        throw new Error(
          'No quote clause template is active. The tenant admin loads one in Company, Documents and clauses.',
        );
      if (!docs.profile)
        throw new Error(
          'The company details are not set up. The tenant admin enters them in Company, Details and payment.',
        );
      const bundle = buildQuoteDocument({
        agg: snapshot,
        clauses: quoteClauses.template,
        profile: docs.profile,
      });
      downloadPdf(await renderPdf(bundle, browserAssets), documentFileName(snapshot, '', 'quote'));
      return `${documentFileName(snapshot, '', 'quote')} is ready.`;
    });

  return (
    <div className="stack">
      <h2 className="heading">Review and send</h2>
      {issues.length === 0 ? (
        <p className="notice" role="status">
          Everything needed to send revision {agg.quote.revision} is filled in.
        </p>
      ) : (
        <IssueList title={`${issues.length} to fix before sending`} issues={issues} />
      )}
      <LineKmFlags agg={agg} />
      {hasOptions(agg) ? (
        <OptionsTable agg={agg} docs={docs} />
      ) : (
        <Totals agg={optionAggregate(agg, optionsOf(agg)[0]!.id)} docs={docs} />
      )}
      <div className="row row--wrap">
        <Button busy={busy === 'pdf'} onClick={() => void pdf(agg, 'pdf')}>
          {busy === 'pdf' ? 'Preparing the PDF…' : 'Download quote PDF (draft)'}
        </Button>
        {send && (
          <Button
            variant="primary"
            busy={busy === 'send'}
            disabled={issues.length > 0 || dirty}
            title={dirty ? 'Save the draft first.' : undefined}
            onClick={() =>
              void act('send', async () => {
                await sendRevision(sb, agg, userId);
                onChanged();
                return `Revision ${agg.quote.revision} is sent and kept as it is. Changes from now on are revision ${agg.quote.revision + 1}.`;
              })
            }
          >
            {busy === 'send' ? 'Sending…' : `Send revision ${agg.quote.revision}`}
          </Button>
        )}
      </div>
      {dirty && send && <p className="caption muted">Save the draft before sending.</p>}

      <h3 className="heading">Revisions sent</h3>
      {revisions.length === 0 && <p className="body muted">No revision has been sent yet.</p>}
      {revisions.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th className="label">Revision</th>
              <th className="label">Sent</th>
              <th className="label">Status</th>
              <th className="label">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {revisions.map((r) => (
              <tr key={r.id}>
                <td className="data">{r.revision}</td>
                <td className="data">{r.sent_at.slice(0, 10)}</td>
                <td>{QUOTE_STATUS_LABEL[r.status]}</td>
                <td className="table__actions">
                  <Button
                    variant="quiet"
                    onClick={() => void pdf(r.snapshot, `pdf-${r.revision}`)}
                    aria-label={`Download revision ${r.revision}`}
                  >
                    Download
                  </Button>
                  {send && r.status === 'sent' && (
                    <>
                      <Button
                        variant="quiet"
                        aria-label={`Accept revision ${r.revision}`}
                        onClick={() =>
                          hasOptions(r.snapshot)
                            ? setAcceptFor(r)
                            : void act('accept', async () => {
                                await setRevisionStatus(
                                  sb,
                                  r,
                                  'accepted',
                                  userId,
                                  optionsOf(r.snapshot)[0]!.id || null,
                                );
                                onChanged();
                                return `Revision ${r.revision} is accepted. Make the contract from it.`;
                              })
                        }
                      >
                        Accept
                      </Button>
                      <Button
                        variant="quiet"
                        aria-label={`Decline revision ${r.revision}`}
                        onClick={() =>
                          void act('decline', async () => {
                            await setRevisionStatus(sb, r, 'declined', userId);
                            onChanged();
                            return `Revision ${r.revision} is marked declined.`;
                          })
                        }
                      >
                        Declined
                      </Button>
                    </>
                  )}
                  {send && r.status === 'accepted' && (
                    <Button
                      variant="primary"
                      onClick={() => setContractFor(r)}
                      aria-label={`Make contract from revision ${r.revision}`}
                    >
                      Make contract
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {acceptFor && (
        <AcceptOption
          revision={acceptFor}
          busy={busy === 'accept'}
          onCancel={() => setAcceptFor(null)}
          onAccept={(chosen) =>
            void act('accept', async () => {
              await setRevisionStatus(sb, acceptFor, 'accepted', userId, chosen);
              setAcceptFor(null);
              onChanged();
              const o = optionsOf(acceptFor.snapshot).find((x) => x.id === chosen)!;
              return `Revision ${acceptFor.revision} is accepted with ${optionTitle(o)}. Make the contract from it.`;
            })
          }
        />
      )}
      {contractFor && (
        <MakeContract
          sb={sb}
          tenantId={tenantId}
          userId={userId}
          revision={contractFor}
          template={contractClauses}
          onCancel={() => setContractFor(null)}
          onMade={onOpenContract}
        />
      )}
    </div>
  );
}

/** The client chooses one option (decision 171); the contract is made from it. */
function AcceptOption({
  revision,
  busy,
  onCancel,
  onAccept,
}: {
  readonly revision: QuoteRevision;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onAccept: (optionId: string) => void;
}): ReactElement {
  const options = optionsOf(revision.snapshot);
  const [chosen, setChosen] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="panel stack"
      aria-label="Accept revision"
      onSubmit={(e) => {
        e.preventDefault();
        try {
          onAccept(acceptedOptionId(revision.snapshot, chosen || null));
        } catch (err: unknown) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }}
    >
      <h3 className="heading">Accept revision {revision.revision}</h3>
      <p className="body muted">
        The revision offers {options.length} options. Choose the one the client accepted; the contract is made
        from that option only.
      </p>
      <SelectField
        label="Option the client chose"
        value={chosen}
        onChange={setChosen}
        error={error ?? undefined}
        options={[['', 'Choose the option'], ...options.map((o) => [o.id, optionTitle(o)] as const)]}
      />
      <div className="row">
        <Button type="submit" variant="primary" busy={busy}>
          Record acceptance
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function MakeContract({
  sb,
  tenantId,
  userId,
  revision,
  template,
  onCancel,
  onMade,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly revision: QuoteRevision;
  readonly template: ReturnType<typeof activeClauses>;
  readonly onCancel: () => void;
  readonly onMade: (id: string) => void;
}): ReactElement {
  const q = revision.snapshot.quote;
  // decision 171: the contract is made from the option the client chose
  const optionId = revision.chosen_option_id ?? (optionsOf(revision.snapshot)[0]!.id || null);
  const chosen = optionsOf(revision.snapshot).find((o) => o.id === optionId) ?? null;
  const [number, setNumber] = useState(jobNumberFromQuote(q.number, q.job_type) ?? '');
  const [date, setDate] = useState(localIsoDate());
  const [regos, setRegos] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setError(null);
    if (!template) {
      setError(
        'No contract clause template is active. The tenant admin loads one in Company, Documents and clauses.',
      );
      return;
    }
    setBusy(true);
    try {
      const projects = (await sb.from('project').select('number').eq('tenant_id', tenantId)).data as
        { number: string }[] | null;
      const issues = validateJobNumber(
        number,
        (projects ?? []).map((p) => p.number),
      );
      if (issues.length) throw new Error(issues[0]!.message);
      const snapshot = contractSnapshot(
        revision.snapshot,
        template.template,
        regos
          .split(',')
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
        optionId,
      );
      const id = await makeContract(sb, {
        tenantId,
        quoteId: q.id,
        revision: revision.revision,
        number: number.trim().toUpperCase(),
        contractDate: date,
        templateVersionId: template.id,
        snapshot,
        optionId,
        userId,
      });
      onMade(id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="panel stack"
      aria-label="Make contract"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h3 className="heading">
        Make contract from revision {revision.revision}
        {hasOptions(revision.snapshot) && chosen ? `, ${optionTitle(chosen)}` : ''}
      </h3>
      <p className="body muted">
        Every value of the revision is copied, with the clause template {template?.version ?? '(none active)'}
        ; nothing is typed again.
      </p>
      <div className="form-grid">
        <TextField
          label="Job number"
          spellCheck={false}
          value={number}
          onChange={setNumber}
          hint="The quote digits with F or H."
        />
        <TextField label="Agreement date" type="date" value={date} onChange={setDate} />
        <TextField
          label="Aircraft registrations"
          spellCheck={false}
          hint="For example VH-JOZ; separate several with commas."
          value={regos}
          onChange={setRegos}
        />
      </div>
      {error && (
        <p className="field__error caption" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <Button type="submit" variant="primary" busy={busy}>
          {busy ? 'Making the contract…' : 'Make contract'}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
